import React, { useState, useEffect, useCallback } from "react";
import styles from "./WelfarePoint.module.css";
import api from "../../api";

export default function WelfarePoint() {
  const [year, setYear] = useState(2026);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // 유저 정보
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const canEdit =
    user.employee_type === "ceo" ||
    (user.employee_type === "regular" && user.grade <= 7);

  // 사용내역 입력 상태
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState({
    user: "",
    used_date: "",
    category: "",
    amount: "",
    memo: "",
    year,
  });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/welfare/summary/?year=${year}`);
      setData(res.data);
    } catch (err) {
      console.error("❌ 복지포인트 조회 실패:", err);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 폼 초기화
  const resetForm = () => {
    setForm({ user: "", used_date: "", category: "", amount: "", memo: "", year });
    setEditTarget(null);
    setShowForm(false);
  };

  // 저장
  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: Number(String(form.amount).replace(/,/g, "")),
        year,
      };
      if (editTarget) {
        await api.patch(`/welfare/usages/${editTarget}/`, payload);
        alert("✅ 수정 완료!");
      } else {
        await api.post("/welfare/usages/", payload);
        alert("✅ 저장 완료!");
      }
      resetForm();
      fetchData();
    } catch (err) {
      console.error("❌ 저장 실패:", err);
      alert("저장 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  };

  // 삭제
  const onDelete = async (id) => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    try {
      await api.delete(`/welfare/usages/${id}/`);
      alert("✅ 삭제 완료!");
      fetchData();
    } catch {
      alert("삭제 중 오류가 발생했습니다.");
    }
  };

  // 수정 클릭
  const onEdit = (usage) => {
    setEditTarget(usage.id);
    setForm({
      user: usage.user,
      used_date: usage.used_date,
      category: usage.category,
      amount: usage.amount.toLocaleString(),
      memo: usage.memo || "",
      year,
    });
    setShowForm(true);
  };

  // 금액 입력 포맷
  const handleAmountChange = (val) => {
    const num = val.replace(/[^\d]/g, "");
    const formatted = num.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    setForm((prev) => ({ ...prev, amount: formatted }));
  };

  // 합계 계산
  const totalPoint  = data?.summary?.reduce((s, r) => s + r.total_point, 0) ?? 0;
  const totalUsed   = data?.summary?.reduce((s, r) => s + r.used_total, 0) ?? 0;
  const totalRemain = data?.summary?.reduce((s, r) => s + r.remain_point, 0) ?? 0;

  if (loading) return <div className={styles.loading}>로딩 중...</div>;

  return (
    <div className={styles.wrap}>
      {/* ── 헤더 ── */}
      <div className={styles.header}>
        <h3>{year}년 복지포인트 지급현황</h3>
        <select
          className={styles.yearSelect}
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
        >
          {[2024, 2025, 2026].map((y) => (
            <option key={y} value={y}>{y}년</option>
          ))}
        </select>
      </div>

      {/* ── 기준 정보 ── */}
      {data?.config ? (
        <div className={styles.configBox}>
          <span>
            기본 <b>{data.config.base_point.toLocaleString()}</b>원 + 변동
            (근속연수×<b>{data.config.tenure_bonus.toLocaleString()}</b>원)
          </span>
          <table className={styles.configTable}>
            <tbody>
              <tr>
                <th>기준일</th>
                <td>{data.config.base_date}</td>
                <th>기본포인트</th>
                <td>{data.config.base_point.toLocaleString()}</td>
                <th>근속가산(연당)</th>
                <td>{data.config.tenure_bonus.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className={styles.noConfig}>⚠️ {year}년 기준 설정이 없습니다. 관리자에서 등록해주세요.</div>
      )}

      {/* ── 지급현황 테이블 ── */}
      <div className={styles.tableWrap}>
        <table className={`${styles.table} ${styles.summaryTable}`}>


          <thead>
            <tr>
              <th>성명</th>
              <th>입사일</th>
              <th>근속연수</th>
              <th>기본포인트</th>
              <th>근속가산</th>
              <th>총 복지포인트</th>
              <th>사용합계</th>
              <th>잔여포인트</th>
            </tr>
          </thead>
          <tbody>
            {data?.summary?.map((row) => (
              <tr key={row.id}>
                <td>{row.name}</td>
                <td>{row.join_date}</td>
                <td>{row.tenure_years}년</td>
                <td>{row.base_point.toLocaleString()}</td>
                <td>{row.tenure_bonus.toLocaleString()}</td>
                <td>{row.total_point.toLocaleString()}</td>
                <td>{row.used_total.toLocaleString()}</td>
                <td className={row.remain_point === 0 ? styles.zero : styles.remain}>
                  {row.remain_point.toLocaleString()}
                </td>
              </tr>
            ))}
            <tr className={styles.totalRow}>
              <td colSpan={5}>합계</td>
              <td>{totalPoint.toLocaleString()}</td>
              <td>{totalUsed.toLocaleString()}</td>
              <td>{totalRemain.toLocaleString()}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── 사용내역 ── */}
      <div className={styles.usageHeader}>
        <h3>복지포인트 사용내역</h3>
      </div>

      {/* ── 폼 + 테이블 나란히 ── */}
      <div className={canEdit ? styles.usageLayout : ""}>

        {/* ── 입력 폼 ── */}
        {canEdit && (
          <div className={styles.formBox}>




            <h4>{editTarget ? "사용내역 수정" : "사용내역 입력"}</h4>
            <form onSubmit={onSubmit} className={styles.form}>
              <div className={styles.formRow}>
                <label>대상자</label>
                <select
                  value={form.user}
                  onChange={(e) => setForm((p) => ({ ...p, user: e.target.value }))}
                  required
                >
                  <option value="">선택</option>
                  {data?.summary?.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formRow}>
                <label>사용일자</label>
                <input
                  type="date"
                  value={form.used_date}
                  onChange={(e) => setForm((p) => ({ ...p, used_date: e.target.value }))}
                  required
                />
              </div>
              <div className={styles.formRow}>
                <label>사용항목</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                  required
                >
                  <option value="">선택</option>
                  <option value="자기계발">자기계발</option>
                  <option value="가족친화">가족친화</option>
                  <option value="건강관리">건강관리</option>
                  <option value="가족친화, 건강관리">가족친화, 건강관리</option>
                  <option value="여가활동">여가활동</option>
                  <option value="기타">기타</option>
                </select>
              </div>
              <div className={styles.formRow}>
                <label>사용금액</label>
                <input
                  type="text"
                  value={form.amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  placeholder="0"
                  required
                />
              </div>
              <div className={styles.formRow}>
                <label>비고</label>
                <input
                  type="text"
                  value={form.memo}
                  onChange={(e) => setForm((p) => ({ ...p, memo: e.target.value }))}
                />
              </div>

              <div className={styles.formBtns}>
                <button type="submit" className={styles.saveBtn} disabled={saving}>
                  {saving ? "저장 중..." : editTarget ? "수정" : "저장"}
                </button>
                {editTarget && (
                  <button type="button" className={styles.cancelBtn} onClick={resetForm}>
                    취소
                  </button>
                )}
              </div>


            </form>
          </div>
        )}

        {/* ── 사용내역 테이블 ── */}
        <div className={styles.tableWrap}>
          <table className={`${styles.table} ${styles.usageTable}`}>

            <thead>
              <tr>
                <th>연번</th>
                <th>대상</th>
                <th>사용일자</th>
                <th>사용항목</th>
                <th>사용금액</th>
                {canEdit && <th>관리</th>}
              </tr>
            </thead>
            <tbody>
              {data?.usages?.length === 0 ? (
                <tr>
                  <td colSpan={canEdit ? 6 : 5} className={styles.empty}>
                    사용내역이 없습니다.
                  </td>
                </tr>
              ) : (
                data?.usages?.map((u, i) => (
                  <tr key={u.id}>
                    <td>{i + 1}</td>
                    <td>{u.user_name}</td>
                    <td>{u.used_date}</td>
                    <td>{u.category}</td>
                    <td>{u.amount.toLocaleString()}</td>
                    {canEdit && (
                      <td className={styles.manageTd}>
                        <button className={styles.editBtn} onClick={() => onEdit(u)}>수정</button>
                        <button className={styles.deleteBtn} onClick={() => onDelete(u.id)}>삭제</button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

      </div>


    </div>
  );
}